import api from '@/libs/axiosInstance';
import type { ApiResponse, MentionDriveArchiveStateDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Query key factory for drive archive state.
 * Exported so mutations can selectively invalidate from outside the hook.
 */
export const driveArchiveStateQueryKey = (mentionId: string) =>
  ['drive-archive-state', mentionId] as const;

const fetchDriveArchiveState = async (mentionId: string): Promise<MentionDriveArchiveStateDto> => {
  const response = await api.get<ApiResponse<MentionDriveArchiveStateDto>>(
    `/dashboard/mentions/${mentionId}/archive-state`
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }

  throw new Error(response.data.message || 'Failed to fetch archive state');
};

/**
 * Reads the Drive archive state for a single mention.
 * Backend exposes this once the brand has at least one IN_PROGRESS / ARCHIVED entry,
 * but a 200 with status=null is also a valid "never archived" response.
 */
export const useDriveArchiveStateQuery = (mentionId: string) => {
  return useQuery<MentionDriveArchiveStateDto, Error>({
    queryKey: driveArchiveStateQueryKey(mentionId),
    queryFn: () => fetchDriveArchiveState(mentionId),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(mentionId),
  });
};
